import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { Shield, Check, ArrowRight, BookOpen, Gift, Lock, Heart, Trophy, Star } from 'lucide-react';
import { PageType } from '../App';
interface SalesPageProps {
  navigate: (page: PageType) => void;
}
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay
}} className={className} data-id="element-4">
    {children}
  </motion.div>;
export function SalesPage({
  navigate
}: SalesPageProps) {
  const [selectedOffer, setSelectedOffer] = useState<'base' | 'pro'>('pro');
  return <div className="min-h-screen bg-navy-900 font-sans text-offwhite overflow-x-hidden" data-id="element-5">
      {/* HERO SECTION */}
      <section className="relative pt-12 pb-16 px-4 md:pt-20 md:pb-24 border-b border-gold-500/20 min-h-screen flex flex-col justify-center" data-id="element-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-navy-700/40 via-navy-900 to-navy-900 -z-10" data-id="element-7"></div>
        <div className="max-w-5xl mx-auto text-center" data-id="element-8">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} data-id="element-9">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif text-white leading-tight mb-8 drop-shadow-lg" data-id="element-10">
              ¿Cuántas veces más vas a apretar "Presentar" y rezar para que AFIP
              no encuentre nada?
            </h1>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.8
          }} className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(212,175,55,0.15)] border border-gold-500/20 mb-10" data-id="element-11">
              <img src="https://cdn.magicpatterns.com/uploads/2mfVS9JknQHDvLkAsHpGpV/portadita...png" alt="El Blindaje Operativo AFIP - Pack 5 Tomos + Bonos" className="w-full h-auto object-cover" data-id="element-12" />
            </motion.div>

            <p className="text-xl md:text-2xl text-gray-300 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-13">
              Hay contadores que duermen tranquilos después de cada cierre. Y
              hay contadores que esperan con ansiedad.{' '}
              <span className="text-gold-400 font-semibold italic" data-id="element-14">
                La diferencia es un sistema.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-20 px-4 bg-navy-900" data-id="element-15">
        <div className="max-w-3xl mx-auto" data-id="element-16">
          <FadeIn data-id="element-17">
            <h2 className="text-3xl md:text-4xl font-bold text-gold-500 font-serif mb-12 text-center" data-id="element-18">
              EL PROBLEMA
            </h2>
          </FadeIn>

          <div className="space-y-6 text-lg md:text-xl text-gray-300 leading-relaxed font-light" data-id="element-19">
            <FadeIn delay={0.1} data-id="element-20">
              <p data-id="element-21">Son las 11 de la noche.</p>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-22">
              <p data-id="element-23">Tenés el cursor sobre "Presentar".</p>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-24">
              <p data-id="element-25">Revisaste dos veces. Creés que está todo bien.</p>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-26">
              <p data-id="element-27">
                Pero algo en tu cabeza dice:{' '}
                <span className="text-gold-400 italic font-serif" data-id="element-28">
                  ¿Y si me equivoqué en algo?
                </span>
              </p>
            </FadeIn>
            <FadeIn delay={0.5} data-id="element-29">
              <p data-id="element-30">Apretás. Y esperás.</p>
            </FadeIn>

            <FadeIn delay={0.6} className="pt-4" data-id="element-31">
              <p data-id="element-32">
                Esa sensación no es normal. No debería ser parte de tu trabajo.
              </p>
              <p data-id="element-33">Pero se convirtió en tu rutina.</p>
            </FadeIn>

            <FadeIn delay={0.2} className="pt-8" data-id="element-34">
              <p className="text-white font-medium mb-6" data-id="element-35">
                Y cuando algo sale mal, sabés exactamente lo que viene:
              </p>
              <ul className="space-y-4 pl-4 border-l-2 border-red-500/50" data-id="element-36">
                <li className="flex items-start" data-id="element-37">
                  <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-38" />{' '}
                  El requerimiento que llega sin aviso
                </li>
                <li className="flex items-start" data-id="element-39">
                  <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-40" />{' '}
                  La multa que no esperabas
                </li>
                <li className="flex items-start" data-id="element-41">
                  <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-42" />{' '}
                  El cliente que llama furioso
                </li>
                <li className="flex items-start" data-id="element-43">
                  <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-44" />{' '}
                  Las horas perdidas resolviendo lo que podrías haber evitado
                </li>
                <li className="flex items-start" data-id="element-45">
                  <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-46" />{' '}
                  Tu reputación profesional en riesgo
                </li>
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} className="pt-8 text-center bg-navy-800/50 p-8 rounded-2xl border border-navy-700" data-id="element-47">
              <p className="text-2xl text-white font-serif mb-4" data-id="element-48">
                ¿Cuánto te costó eso el año pasado?
              </p>
              <p className="text-gray-400 mb-6" data-id="element-49">
                En tiempo. En dinero. En clientes. En energía.
              </p>
              <p className="text-xl text-gold-500 font-bold" data-id="element-50">
                Si sos honesto... sabés que demasiado.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* TRUTH SECTION */}
      <section className="py-20 px-4 bg-navy-800 border-y border-gold-500/10" data-id="element-51">
        <div className="max-w-3xl mx-auto" data-id="element-52">
          <FadeIn data-id="element-53">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-12 text-center" data-id="element-54">
              LA VERDAD QUE NADIE TE DICE
            </h2>
          </FadeIn>

          <div className="space-y-6 text-lg text-gray-300" data-id="element-55">
            <FadeIn data-id="element-56">
              <p data-id="element-57">El problema no es que seas mal contador.</p>
              <p className="text-xl text-white font-bold mt-2" data-id="element-58">
                El problema es que trabajás sin un protocolo de prevención.
              </p>
            </FadeIn>

            <FadeIn data-id="element-59">
              <div className="my-10 text-center" data-id="element-60">
                <h3 className="text-2xl md:text-3xl font-bold text-gold-400 font-serif leading-tight" data-id="element-61">
                  El 80% de los errores que generan problemas con AFIP son
                  errores sistémicos y preventibles.
                </h3>
              </div>
            </FadeIn>

            <FadeIn data-id="element-62">
              <div className="bg-navy-900 p-8 rounded-2xl border-l-4 border-gold-500" data-id="element-63">
                <p className="mb-4" data-id="element-64">No son errores técnicos complejos.</p>
                <p className="mb-4" data-id="element-65">
                  Son errores que ocurren porque no existe una revisión
                  estructurada antes de cada presentación.
                </p>
                <p className="text-white font-medium" data-id="element-66">
                  AFIP no te avisa. Te deja presentar. Y después llega el
                  requerimiento.
                </p>
              </div>
            </FadeIn>

            <FadeIn data-id="element-67">
              <div className="text-center space-y-4 text-xl mt-12" data-id="element-68">
                <p className="text-gray-400" data-id="element-69">
                  La solución no es trabajar más horas.
                </p>
                <p className="text-gold-500 font-bold text-2xl mt-4" data-id="element-70">
                  La solución es tener un sistema de blindaje preventivo.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PRODUCT INTRO */}
      <section className="py-20 px-4 bg-navy-900 relative overflow-hidden" data-id="element-71">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-[100px] pointer-events-none" data-id="element-72"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-73">
          <FadeIn data-id="element-74">
            <div className="flex justify-center mb-6" data-id="element-75">
              <Shield className="w-16 h-16 text-gold-500" data-id="element-76" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white font-serif mb-4 drop-shadow-lg" data-id="element-77">
              EL BLINDAJE OPERATIVO AFIP
            </h1>
            <h3 className="text-xl md:text-2xl text-gold-400 font-light mb-2" data-id="element-78">
              El Protocolo de Auditoría Preventiva Anti-SIAp
            </h3>
            <p className="text-lg text-gray-400 italic mb-12" data-id="element-79">
              Pack 5 Tomos + 3 Bonos — by Fegion
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-80">
            <div className="max-w-2xl mx-auto text-center space-y-4 text-lg text-gray-300 bg-navy-800/50 p-8 rounded-2xl border border-gold-500/20" data-id="element-81">
              <p data-id="element-82">No es un curso. No es teoría. No es normativa resumida.</p>
              <p className="text-xl text-white font-bold text-gold-500" data-id="element-83">
                Es tu protocolo operativo. Tu sistema. Tu escudo.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 5 TOMOS SECTION */}
      <section className="py-16 px-4 bg-navy-800" data-id="element-84">
        <div className="max-w-4xl mx-auto" data-id="element-85">
          <FadeIn data-id="element-86">
            <div className="text-center mb-12" data-id="element-87">
              <BookOpen className="w-10 h-10 text-gold-500 mx-auto mb-4" data-id="element-88" />
              <h2 className="text-3xl font-bold text-white font-serif mb-4" data-id="element-89">
                LOS 5 TOMOS
              </h2>
            </div>
          </FadeIn>

          <div className="space-y-6" data-id="element-90">
            {[{
            num: '1',
            title: 'Fundamentos Operativos y Conceptuales',
            desc: 'Cómo funciona AFIP por dentro. Qué busca. Qué detecta. Cómo posicionarte un paso adelante.'
          }, {
            num: '2',
            title: 'Estrategias Fiscales Avanzadas y Litigio',
            desc: 'Cómo documentar, responder y proteger a tu cliente ante cualquier situación compleja.'
          }, {
            num: '3',
            title: 'Fiscalización y Defensoría',
            desc: 'Qué hacer cuando AFIP inicia un proceso. Pasos exactos. En orden. Sin improvisar.'
          }, {
            num: '4',
            title: 'Estrategias de Defensa',
            desc: 'El arsenal completo para los escenarios más frecuentes de tu estudio contable.'
          }, {
            num: '5',
            title: 'Implementación y Cierre Operativo',
            desc: 'Cómo instalar el sistema en tu rutina semanal en menos de 20 minutos.'
          }].map((tomo, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-91">
                <div className="bg-navy-900 border-l-4 border-gold-500 rounded-r-xl p-6 shadow-md" data-id="element-92">
                  <h3 className="text-xl font-bold text-white font-serif mb-2" data-id="element-93">
                    <span className="text-gold-500 mr-2" data-id="element-94">
                      📘 TOMO {tomo.num} —
                    </span>{' '}
                    {tomo.title}
                  </h3>
                  <p className="text-gray-300" data-id="element-95">{tomo.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 3 BONOS SECTION */}
      <section className="py-16 px-4 bg-navy-900 border-t border-gold-500/10" data-id="element-96">
        <div className="max-w-4xl mx-auto" data-id="element-97">
          <FadeIn data-id="element-98">
            <div className="text-center mb-12" data-id="element-99">
              <Gift className="w-10 h-10 text-gold-500 mx-auto mb-4" data-id="element-100" />
              <h2 className="text-3xl font-bold text-white font-serif mb-4" data-id="element-101">
                LOS 3 BONOS INCLUIDOS
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-102">
            <FadeIn delay={0.1} className="h-full" data-id="element-103">
              <div className="bg-navy-800 border-t-4 border-green-500 rounded-b-xl p-6 h-full shadow-lg" data-id="element-104">
                <h3 className="text-lg font-bold text-white font-serif mb-3" data-id="element-105">
                  🟢 BONO 1<br data-id="element-106" />
                  <span className="text-green-400" data-id="element-107">DFE en 3 Minutos</span>
                </h3>
                <p className="text-sm text-gray-300" data-id="element-108">
                  El protocolo semanal para revisar tu Domicilio Fiscal
                  Electrónico y nunca más perderte una notificación de AFIP.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="h-full" data-id="element-109">
              <div className="bg-navy-800 border-t-4 border-blue-500 rounded-b-xl p-6 h-full shadow-lg" data-id="element-110">
                <h3 className="text-lg font-bold text-white font-serif mb-3" data-id="element-111">
                  🔵 BONO 2<br data-id="element-112" />
                  <span className="text-blue-400" data-id="element-113">Checklist Anti-Rechazo</span>
                </h3>
                <p className="text-sm text-gray-300" data-id="element-114">
                  27 controles exactos antes de apretar "Presentar". Cero
                  rechazos evitables.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.3} className="h-full" data-id="element-115">
              <div className="bg-navy-800 border-t-4 border-amber-500 rounded-b-xl p-6 h-full shadow-lg" data-id="element-116">
                <h3 className="text-lg font-bold text-white font-serif mb-3" data-id="element-117">
                  🟤 BONO 3<br data-id="element-118" />
                  <span className="text-amber-400" data-id="element-119">
                    Mapa Express de Servicios
                  </span>
                </h3>
                <p className="text-sm text-gray-300" data-id="element-120">
                  Qué cobrar, cuándo y cómo. Para dejar de regalar trabajo esta
                  semana.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-16 px-4 bg-navy-800" data-id="element-121">
        <div className="max-w-3xl mx-auto" data-id="element-122">
          <FadeIn data-id="element-123">
            <h2 className="text-3xl font-bold text-center text-white font-serif mb-10" data-id="element-124">
              LO QUE CAMBIA EN TU TRABAJO
            </h2>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-125">
            <div className="rounded-2xl border border-gold-500/30 shadow-xl overflow-hidden" data-id="element-126">
              <table className="w-full text-left border-collapse" data-id="element-127">
                <thead data-id="element-128">
                  <tr className="bg-navy-900 border-b border-gold-500/30" data-id="element-129">
                    <th className="p-3 md:p-5 text-red-400 font-bold text-center w-1/2 text-xs md:text-base" data-id="element-130">
                      ❌ Sin el Blindaje
                    </th>
                    <th className="p-3 md:p-5 text-green-400 font-bold text-center w-1/2 border-l border-navy-700 bg-gold-500/5 text-xs md:text-base" data-id="element-131">
                      ✅ Con el Blindaje
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-700 bg-navy-800 text-xs md:text-base" data-id="element-132">
                  {[{
                  bad: 'Presentás y rezás',
                  good: 'Verificás y presentás'
                }, {
                  bad: 'AFIP encuentra los errores',
                  good: 'Vos los encontrás primero'
                }, {
                  bad: 'Cada cierre genera ansiedad',
                  good: 'Cada cierre es un proceso'
                }, {
                  bad: 'Respondés a problemas',
                  good: 'Prevenís problemas'
                }, {
                  bad: 'Trabajás con memoria',
                  good: 'Trabajás con sistema'
                }].map((row, idx) => <tr key={idx} data-id="element-133">
                      <td className="p-3 md:p-4 text-gray-400 text-center" data-id="element-134">
                        {row.bad}
                      </td>
                      <td className="p-3 md:p-4 text-gold-300 font-medium border-l border-navy-700 text-center bg-gold-500/5" data-id="element-135">
                        {row.good}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 px-4 bg-navy-900 border-t border-gold-500/10" data-id="element-136">
        <div className="max-w-4xl mx-auto" data-id="element-137">
          <FadeIn data-id="element-138">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white font-serif mb-16" data-id="element-139">
              LO QUE DICEN LOS CONTADORES QUE YA LO TIENEN
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-140">
            {[{
            stars: 5,
            title: '"Lo compré sin muchas expectativas y me sorprendió"',
            quote: 'Soy contador hace 9 años y pensé que era otro producto genérico. Pero el checklist de 27 puntos solo ya justificó lo que pagué. La primera semana lo usé antes de tres presentaciones y en una encontré un error que no había visto. Eso no tiene precio.',
            name: 'Martín G.',
            role: 'Contador Público',
            location: 'Rosario, Santa Fe',
            img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face'
          }, {
            stars: 5,
            title: '"El tema del DFE me pegó fuerte"',
            quote: 'Me llegó una notificación de AFIP que tenía 12 días sin ver. Por suerte llegué a tiempo, pero fue un llamado de atención. Desde que uso el protocolo de los 3 minutos los lunes, eso no me volvió a pasar. Simple pero muy efectivo.',
            name: 'Claudia R.',
            role: 'Contadora Pública Independiente',
            location: 'CABA',
            img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face'
          }, {
            stars: 5,
            title: '"Muy completo para el precio que tiene"',
            quote: 'Honestamente esperaba algo más básico. Los 5 tomos están bien estructurados y se nota que fue pensado para la realidad argentina, no para cualquier mercado. El tomo de fiscalización y defensoría lo leí dos veces.',
            name: 'Diego F.',
            role: 'Estudio Contable',
            location: 'Córdoba Capital',
            img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
          }, {
            stars: 5,
            title: '"Me hizo dar cuenta de cuánto estaba regalando"',
            quote: 'El bono del mapa de servicios me abrió los ojos. Tenía clientes a los que les hacía gestiones adicionales hace meses sin cobrarlas. Lo reordené todo en una semana. Ya recuperé lo que pagué con el primer cliente al que le facturé correctamente.',
            name: 'Valeria M.',
            role: 'Contadora',
            location: 'Mar del Plata',
            img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
          }, {
            stars: 4,
            title: '"El tomo de defensa es muy práctico"',
            quote: 'Tuve una situación con un cliente y un requerimiento de AFIP justo después de comprar el pack. El tomo 4 me ayudó a ordenar la respuesta. No digo que fue por el libro solo, pero tener el protocolo claro me dio más seguridad para manejarlo.',
            name: 'Fernando T.',
            role: 'Contador Público',
            location: 'Tucumán',
            img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face'
          }].map((t, idx) => <FadeIn key={idx} delay={idx * 0.08} data-id="element-141">
                <div className="bg-navy-800 p-6 rounded-2xl border border-navy-700 h-full flex flex-col" data-id="element-142">
                  <div className="flex text-gold-500 mb-3" data-id="element-143">
                    {[...Array(5)].map((_, i) => <svg key={i} className={`w-4 h-4 ${i < t.stars ? 'fill-gold-500 text-gold-500' : 'fill-navy-600 text-navy-600'}`} viewBox="0 0 20 20" data-id="element-144">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-145" />
                      </svg>)}
                  </div>
                  <h4 className="text-white font-bold mb-3 text-sm" data-id="element-146">
                    {t.title}
                  </h4>
                  <p className="text-gray-400 text-sm italic leading-relaxed flex-1" data-id="element-147">
                    "{t.quote}"
                  </p>
                  <div className="mt-5 pt-4 border-t border-navy-700 flex items-center gap-3" data-id="element-148">
                    <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover border-2 border-navy-600" data-id="element-149" />
                    <div data-id="element-150">
                      <p className="text-white font-bold text-sm" data-id="element-151">{t.name}</p>
                      <p className="text-gray-500 text-xs" data-id="element-152">
                        {t.role} · {t.location}
                      </p>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* UPSELL INTRO */}
      <section className="py-20 px-4 bg-navy-900 border-t border-gold-500/20 relative overflow-hidden" data-id="element-153">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-[120px] pointer-events-none" data-id="element-154"></div>
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-155">
          <FadeIn data-id="element-156">
            <div className="text-center mb-12" data-id="element-157">
              <span className="text-4xl mb-4 block" data-id="element-158">⚡</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-3" data-id="element-159">
                MOMENTO — ANTES DE CONFIRMAR TU COMPRA
              </h2>
              <p className="text-xl text-gold-400 italic" data-id="element-160">
                Agregá esto a tu pack y completá el sistema en un solo paso
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-161">
            <div className="text-center mb-12" data-id="element-162">
              <p className="text-lg text-gray-300 mb-2" data-id="element-163">
                El Blindaje te protege de AFIP.
              </p>
              <h3 className="text-3xl md:text-4xl font-bold text-white font-serif" data-id="element-164">
                Pero, ¿quién protege tu rentabilidad?
              </h3>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-165">
            <div className="space-y-5 text-lg text-gray-300 mb-12" data-id="element-166">
              <p data-id="element-167">Trabajás más que nunca.</p>
              <p data-id="element-168">Tenés más clientes que antes.</p>
              <p className="text-white font-medium" data-id="element-169">
                Y sin embargo... los números a fin de mes no reflejan todo ese
                esfuerzo.
              </p>
              <p className="mt-6" data-id="element-170">¿Por qué?</p>
              <p data-id="element-171">
                Porque{' '}
                <strong className="text-white" data-id="element-172">blindar la operación</strong> y{' '}
                <strong className="text-white" data-id="element-173">
                  gestionar la rentabilidad
                </strong>{' '}
                son dos cosas distintas.
              </p>
              <p data-id="element-174">Y la mayoría de los contadores solo trabajan en una.</p>
            </div>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-175">
            <div className="text-center mb-10" data-id="element-176">
              <p className="text-gold-500 font-bold tracking-widest uppercase text-sm mb-4" data-id="element-177">
                🏆 MEJORÁ TU PACK CON:
              </p>
              <h2 className="text-5xl md:text-6xl font-bold text-white font-serif mb-3" data-id="element-178">
                ESTUDIO 10X
              </h2>
              <h3 className="text-xl md:text-2xl text-gold-400 mb-1" data-id="element-179">
                Master de Gestión, Honorarios y Rentabilidad
              </h3>
              <p className="text-gray-400 italic" data-id="element-180">
                Para el Contador Público Argentino
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-181">
            <div className="bg-navy-800/60 border border-navy-600 rounded-2xl p-8 space-y-5" data-id="element-182">
              {[{
              title: 'Sistema de honorarios',
              desc: 'Cuánto cobrar por cada servicio, sin subestimarte'
            }, {
              title: 'Mapa de rentabilidad',
              desc: 'Cuáles clientes te dan dinero y cuáles te lo sacan'
            }, {
              title: 'Protocolo de cobro',
              desc: 'Cobrar a tiempo, sin perseguir a nadie'
            }, {
              title: 'Servicios adicionales',
              desc: 'Qué ofrecer y cómo presentarlo para que el cliente diga que sí'
            }, {
              title: 'Gestión del estudio',
              desc: 'Trabajar menos horas con más resultado'
            }].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-183">
                  <Check className="w-6 h-6 text-green-500 shrink-0 mt-0.5" data-id="element-184" />
                  <p className="text-gray-200" data-id="element-185">
                    <strong className="text-white" data-id="element-186">{item.title}</strong> —{' '}
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER CARDS */}
      <section className="py-20 px-4 bg-navy-900 relative" data-id="element-187">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold-900/10 via-navy-900 to-navy-900 -z-10" data-id="element-188"></div>
        <div className="max-w-4xl mx-auto" data-id="element-189">
          <FadeIn data-id="element-190">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white font-serif mb-4" data-id="element-191">
              ELEGÍ TU OPCIÓN
            </h2>
            <p className="text-center text-gray-400 mb-12" data-id="element-192">
              Seleccioná el pack que mejor se adapte a tu estudio.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-193">
            <div className="max-w-md mx-auto bg-[#111827] p-4 md:p-6 rounded-[2rem] border border-navy-600 shadow-2xl" data-id="element-194">
              {/* Pro Card (First) */}
              <div onClick={() => setSelectedOffer('pro')} className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all mb-4 flex justify-between items-start ${selectedOffer === 'pro' ? 'border-green-500 bg-navy-800/80 shadow-[0_0_15px_rgba(34,197,94,0.15)]' : 'border-navy-600 bg-navy-800/40 hover:border-navy-500'}`} data-id="element-195">
                <div className="flex items-start gap-4" data-id="element-196">
                  <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedOffer === 'pro' ? 'border-green-500' : 'border-gray-500'}`} data-id="element-197">
                    {selectedOffer === 'pro' && <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-198" />}
                  </div>
                  <div data-id="element-199">
                    <div className="flex items-center gap-2 mb-2" data-id="element-200">
                      <Trophy className="w-5 h-5 text-gold-500 fill-gold-500" data-id="element-201" />
                      <span className="font-bold text-white text-lg tracking-wide" data-id="element-202">
                        COMBO PRO
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 mb-3 items-start" data-id="element-203">
                      <span className="bg-green-600 text-white text-[10px] sm:text-xs font-bold px-2 py-1 rounded flex items-center gap-1 uppercase tracking-wider" data-id="element-204">
                        <Star className="w-3 h-3 fill-white" data-id="element-205" /> MÁS ELEGIDO
                      </span>
                      <span className="border border-green-500/50 text-green-400 text-[10px] sm:text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider" data-id="element-206">
                        AHORRAS $10.000
                      </span>
                    </div>
                    <ul className="text-gray-400 text-sm space-y-1.5" data-id="element-207">
                      <li data-id="element-208">5 tomos + 3 bonos</li>
                      <li className="text-white font-medium" data-id="element-209">+ Estudio 10X</li>
                      <li className="text-gray-500 text-xs pl-2" data-id="element-210">
                        - Sistema de Honorarios
                      </li>
                      <li className="text-gray-500 text-xs pl-2" data-id="element-211">
                        - Gestión y Rentabilidad
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="text-right" data-id="element-212">
                  <p className="text-gray-500 line-through text-sm font-medium" data-id="element-213">
                    $39.980
                  </p>
                  <p className="text-3xl font-bold text-green-500" data-id="element-214">$29.990</p>
                </div>
              </div>

              {/* Base Card (Second) */}
              <div onClick={() => setSelectedOffer('base')} className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all mb-6 flex justify-between items-center ${selectedOffer === 'base' ? 'border-green-500 bg-navy-800/80 shadow-[0_0_15px_rgba(34,197,94,0.15)]' : 'border-navy-600 bg-navy-800/40 hover:border-navy-500'}`} data-id="element-215">
                <div className="flex items-start gap-4" data-id="element-216">
                  <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedOffer === 'base' ? 'border-green-500' : 'border-gray-500'}`} data-id="element-217">
                    {selectedOffer === 'base' && <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-218" />}
                  </div>
                  <div data-id="element-219">
                    <div className="flex items-center gap-2 mb-1" data-id="element-220">
                      <Heart className="w-5 h-5 text-green-500 fill-green-500" data-id="element-221" />
                      <span className="font-bold text-white text-lg tracking-wide" data-id="element-222">
                        PACK BASE
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm" data-id="element-223">5 tomos + 3 bonos</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-224">
                  <p className="text-gray-500 line-through text-sm font-medium" data-id="element-225">
                    $59.920
                  </p>
                  <p className="text-2xl font-bold text-white" data-id="element-226">$19.990</p>
                </div>
              </div>

              {/* CTA Button */}
              <a href={selectedOffer === 'base' ? 'https://fegion.shop/cart/46187419336843:1' : 'https://fegion.shop/cart/46187434344587:1'} target="_blank" rel="noopener noreferrer" className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold text-lg py-4 px-6 rounded-xl shadow-[0_0_20px_rgba(46,125,50,0.4)] transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 uppercase tracking-wide" data-id="element-227">
                <Heart className="w-5 h-5 fill-white" data-id="element-228" />
                EMPEZAR HOY — ${selectedOffer === 'base' ? '19.990' : '29.990'}
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PACK COMPARISON TABLE */}
      <section className="py-16 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-229">
        <div className="max-w-3xl mx-auto" data-id="element-230">
          <FadeIn data-id="element-231">
            <div className="rounded-2xl border border-gold-500/30 shadow-xl overflow-hidden" data-id="element-232">
              <table className="w-full text-left border-collapse" data-id="element-233">
                <thead data-id="element-234">
                  <tr className="bg-navy-900 border-b border-gold-500/30" data-id="element-235">
                    <th className="p-2 md:p-5 text-gray-400 font-medium text-xs md:text-base w-[40%]" data-id="element-236"></th>
                    <th className="p-2 md:p-5 text-center border-l border-navy-700 text-xs md:text-base w-[30%]" data-id="element-237">
                      <span className="text-white font-bold" data-id="element-238">
                        <span className="hidden md:inline" data-id="element-239">📦 </span>PACK BASE
                      </span>
                    </th>
                    <th className="p-2 md:p-5 text-center border-l border-navy-700 bg-gold-500/5 text-xs md:text-base w-[30%]" data-id="element-240">
                      <span className="text-gold-400 font-bold" data-id="element-241">
                        <span className="hidden md:inline" data-id="element-242">🏆 </span>BASE + 10X
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-700 bg-navy-800 text-xs md:text-base" data-id="element-243">
                  {[{
                  label: '5 Tomos Blindaje',
                  base: true,
                  pro: true
                }, {
                  label: '3 Bonos',
                  base: true,
                  pro: true
                }, {
                  label: 'Estudio 10X',
                  base: false,
                  pro: true
                }, {
                  label: 'Honorarios',
                  base: false,
                  pro: true
                }, {
                  label: 'Gestión y Rentabilidad',
                  base: false,
                  pro: true
                }].map((row, idx) => <tr key={idx} data-id="element-244">
                      <td className="p-2 md:p-4 text-gray-300 font-medium" data-id="element-245">
                        {row.label}
                      </td>
                      <td className="p-2 md:p-4 text-center border-l border-navy-700" data-id="element-246">
                        {row.base ? <span className="text-green-500" data-id="element-247">✅</span> : <span className="text-red-500" data-id="element-248">❌</span>}
                      </td>
                      <td className="p-2 md:p-4 text-center border-l border-navy-700 bg-gold-500/5" data-id="element-249">
                        {row.pro ? <span className="text-green-500" data-id="element-250">✅</span> : <span className="text-red-500" data-id="element-251">❌</span>}
                      </td>
                    </tr>)}
                  <tr className="bg-navy-900/50 border-t-2 border-gold-500/30" data-id="element-252">
                    <td className="p-2 md:p-4 text-white font-bold text-xs md:text-base" data-id="element-253">
                      PRECIO
                    </td>
                    <td className="p-2 md:p-4 text-center border-l border-navy-700 text-white font-bold text-sm md:text-lg" data-id="element-254">
                      $19.990
                    </td>
                    <td className="p-2 md:p-4 text-center border-l border-navy-700 bg-gold-500/5 text-gold-400 font-bold text-sm md:text-lg" data-id="element-255">
                      $29.990
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </FadeIn>

          {/* Savings callout */}
          <FadeIn delay={0.2} data-id="element-256">
            <div className="mt-10 text-center bg-navy-900/50 p-6 rounded-2xl border border-gold-500/20" data-id="element-257">
              <p className="text-gold-400 font-bold text-xl mb-2" data-id="element-258">
                💡 Solo $10.000 ARS más.
              </p>
              <p className="text-gray-300" data-id="element-259">
                El Estudio 10X por separado vale $19.990 ARS.
              </p>
              <p className="text-gold-400 italic text-sm mt-1" data-id="element-260">
                Agregándolo ahora lo llevás a la mitad del precio.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* DUAL CTA SECTION */}
      <section className="py-20 px-4 bg-navy-900 relative" data-id="element-261">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-green-900/10 via-navy-900 to-navy-900 -z-10" data-id="element-262"></div>
        <div className="max-w-2xl mx-auto text-center" data-id="element-263">
          <FadeIn data-id="element-264">
            <p className="text-red-500 font-bold tracking-widest uppercase text-sm mb-8" data-id="element-265">
              🔴 ELEGÍ TU OPCIÓN:
            </p>

            {/* Option A — Combo Pro */}
            <div className="bg-navy-800 border-2 border-green-500 rounded-2xl p-8 md:p-10 mb-6 shadow-[0_0_30px_rgba(34,197,94,0.1)]" data-id="element-266">
              <p className="text-green-400 font-bold uppercase tracking-wider text-sm mb-2" data-id="element-267">
                OPCIÓN A — PACK COMPLETO RECOMENDADO
              </p>
              <h3 className="text-2xl font-bold text-white font-serif mb-4" data-id="element-268">
                Blindaje AFIP + Estudio 10X
              </h3>
              <div className="flex items-center justify-center gap-3 mb-6" data-id="element-269">
                <span className="text-gray-500 line-through text-xl" data-id="element-270">
                  $39.980
                </span>
                <ArrowRight className="w-5 h-5 text-gold-500" data-id="element-271" />
                <span className="text-3xl font-black text-white font-serif" data-id="element-272">
                  $29.990{' '}
                  <span className="text-base text-gray-400 font-sans font-normal" data-id="element-273">
                    ARS
                  </span>
                </span>
              </div>
              <a href="https://fegion.shop/cart/46187434344587:1" target="_blank" rel="noopener noreferrer" className="w-full block bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold text-lg py-5 px-8 rounded-xl shadow-[0_0_25px_rgba(46,125,50,0.4)] transition-all transform hover:scale-[1.02] uppercase tracking-wide mb-4 text-center" data-id="element-274">
                → SÍ, QUIERO EL COMBO COMPLETO ←
              </a>
              <p className="text-gray-400 text-sm" data-id="element-275">
                ✅ Todo incluido · ✅ Acceso inmediato · ✅ Garantía 7 días
              </p>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 my-8" data-id="element-276">
              <div className="flex-1 h-px bg-navy-700" data-id="element-277"></div>
              <span className="text-gray-500 font-medium" data-id="element-278">— o —</span>
              <div className="flex-1 h-px bg-navy-700" data-id="element-279"></div>
            </div>

            {/* Option B — Pack Base */}
            <div className="mb-8" data-id="element-280">
              <p className="text-gray-400 uppercase tracking-wider text-sm mb-2" data-id="element-281">
                OPCIÓN B — Solo el Pack Base
              </p>
              <p className="text-xl text-white font-bold mb-4" data-id="element-282">$19.990 ARS</p>
              <a href="https://fegion.shop/cart/46187419336843:1" target="_blank" rel="noopener noreferrer" className="w-full block bg-[#D4874D] hover:bg-[#C07843] text-white font-bold text-base py-4 px-8 rounded-xl shadow-[0_0_15px_rgba(212,135,77,0.25)] transition-all transform hover:scale-[1.02] mb-2 text-center" data-id="element-283">
                No gracias, prefiero solo el Blindaje Operativo AFIP
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-284">
        <div className="max-w-2xl mx-auto" data-id="element-285">
          <FadeIn data-id="element-286">
            <div className="bg-navy-900 border border-gold-500/30 rounded-2xl p-8 text-center shadow-lg" data-id="element-287">
              <Lock className="w-12 h-12 text-gold-500 mx-auto mb-4" data-id="element-288" />
              <h2 className="text-2xl font-bold text-white font-serif mb-4" data-id="element-289">
                🔒 GARANTÍA EN AMBAS OPCIONES
              </h2>
              <p className="text-gray-300 text-lg" data-id="element-290">
                7 días de garantía total. Devolución del 100% sin preguntas.
              </p>
              <p className="text-gold-400 font-medium mt-4" data-id="element-291">
                Sea cual sea la opción que elijas — estás completamente
                protegido.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 px-4 bg-navy-900 border-t border-navy-700 text-center" data-id="element-292">
        <Shield className="w-6 h-6 text-gold-500/50 mx-auto mb-3" data-id="element-293" />
        <p className="text-gold-500/70 font-serif italic text-sm" data-id="element-294">
          by Fegion — Para el Contador Público Argentino que trabaja en serio.
        </p>
      </footer>
    </div>;
}